/**
 * Phototron WebGL Shaders
 * PBR Specular Workflow for Material Rendering
 */

export const VERTEX_SHADER = `
  attribute vec2 a_position;
  attribute vec2 a_texCoord;
  varying vec2 v_texCoord;
  
  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
    v_texCoord = a_texCoord;
  }
`;

export const FRAGMENT_SHADER_FULL = `
  precision highp float;

  varying vec2 v_texCoord;

  uniform sampler2D u_albedo;
  uniform sampler2D u_normal;
  uniform sampler2D u_specular;
  uniform sampler2D u_roughnessMap;
  uniform sampler2D u_metalnessMap;
  uniform vec2 u_textureSize;
  uniform vec2 u_canvasSize;
  uniform vec3 u_lightPos;

  // Which maps are actually bound (1.0 = present). Drives the workflow: with a
  // roughness map the 2D reads roughness directly like the GLB; with a metalness
  // map it renders metallic; otherwise it falls back to the dielectric specular
  // (inverted-gloss) workflow so older exports still render.
  uniform float u_hasRoughnessMap;
  uniform float u_hasMetalnessMap;
  uniform float u_hasSpecularMap;

  // 0 = idle (full neutral environment, matches the 3D still), 1 = pointer over
  // the material: the environment dims to a subtle fill and the moving key
  // light takes over — smoothed on the CPU side for a soft handover.
  uniform float u_relight;

  // Configurable lighting parameters
  uniform float u_ambientIntensity;
  uniform float u_diffuseIntensity;
  uniform float u_specularMultiplier;
  uniform float u_fresnelIntensity;
  uniform vec3 u_accentColor;
  uniform float u_accentIntensity;
  uniform float u_materialScale;
  uniform float u_verticalOffset;

  vec3 srgbToLinear(vec3 c) {
    vec3 lo = c / 12.92;
    vec3 hi = pow((c + 0.055) / 1.055, vec3(2.4));
    return mix(lo, hi, step(0.04045, c));
  }

  vec3 linearToSrgb(vec3 c) {
    c = max(c, vec3(0.0));
    vec3 lo = c * 12.92;
    vec3 hi = 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055;
    return mix(lo, hi, step(0.0031308, c));
  }

  // Khronos "PBR Neutral" tone mapping — the exact operator <model-viewer> uses
  // for tone-mapping="neutral", so the 2D tonemapping matches the 3D.
  vec3 pbrNeutralToneMapping(vec3 color) {
    float startCompression = 0.8 - 0.04;
    float desaturation = 0.15;
    float x = min(color.r, min(color.g, color.b));
    float offset = x < 0.08 ? x - 6.25 * x * x : 0.04;
    color -= offset;
    float peak = max(color.r, max(color.g, color.b));
    if (peak < startCompression) return color;
    float d = 1.0 - startCompression;
    float newPeak = 1.0 - d * d / (peak + d - startCompression);
    color *= newPeak / peak;
    float g = 1.0 - 1.0 / (desaturation * (peak - newPeak) + 1.0);
    return mix(color, vec3(newPeak), g);
  }

  vec3 fresnelSchlick(vec3 f0, float vdoth) {
    return f0 + (vec3(1.0) - f0) * pow(1.0 - vdoth, 5.0);
  }

  void main() {
    // Calculate aspect ratios
    float textureAspect = u_textureSize.x / u_textureSize.y;
    float canvasAspect = u_canvasSize.x / u_canvasSize.y;

    vec2 uv = v_texCoord;

    // Apply material scale (zoom)
    uv = (uv - 0.5) / u_materialScale + 0.5;

    // Adjust UV to maintain texture aspect ratio (contain mode)
    if (canvasAspect > textureAspect) {
      float scale = textureAspect / canvasAspect;
      uv.x = (uv.x - 0.5) / scale + 0.5;
      uv.y += u_verticalOffset;
    } else {
      float scale = canvasAspect / textureAspect;
      uv.y = (uv.y - 0.5) / scale + 0.5;
      uv.y += u_verticalOffset / scale;
    }

    if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
      discard;
    }

    vec4 albedoSample = texture2D(u_albedo, uv);
    float alpha = albedoSample.a;
    if (alpha < 0.01) discard;
    // Albedo is an sRGB map — linearise before any lighting math (the 3D
    // pipeline works in linear too; skipping this is a big part of why the old
    // 2D colours looked off).
    vec3 albedo = srgbToLinear(albedoSample.rgb);

    vec3 normal = normalize(texture2D(u_normal, uv).rgb * 2.0 - 1.0);

    // ── Roughness + metalness: prefer the real baked maps (same as the GLB) ──
    float roughness;
    if (u_hasRoughnessMap > 0.5) {
      roughness = texture2D(u_roughnessMap, uv).r;      // non-colour, linear
    } else if (u_hasSpecularMap > 0.5) {
      float gloss = dot(texture2D(u_specular, uv).rgb, vec3(0.299, 0.587, 0.114));
      roughness = 1.0 - gloss;                          // dielectric fallback
    } else {
      roughness = 0.5;
    }
    roughness = clamp(roughness, 0.045, 1.0);

    float metalness = u_hasMetalnessMap > 0.5 ? texture2D(u_metalnessMap, uv).r : 0.0;
    metalness = clamp(metalness, 0.0, 1.0);

    // Metallic-roughness: dielectric F0 = 0.04, metal F0 = albedo; metals have
    // no diffuse.
    vec3 f0 = mix(vec3(0.04), albedo, metalness);
    vec3 diffuseColor = albedo * (1.0 - metalness);

    vec3 Vv = vec3(0.0, 0.0, 1.0);
    float NdotV = max(dot(normal, Vv), 1e-4);
    float up = 0.5 + 0.5 * normal.y;

    // ── Neutral environment (approximates <model-viewer>'s "neutral" IBL) ────
    // Soft, near-uniform irradiance so the albedo reads at its true colour under
    // even light — like the 3D neutral render. u_diffuseIntensity is the overall
    // brightness knob (light-intensity), u_ambientIntensity a soft fill floor.
    // While the pointer relights (u_relight → 1) the environment drops to a
    // faint fill so the key light VISIBLY takes over — deliberately strong
    // ("flashlight" feel): areas the key light misses go noticeably dark.
    float envFade = mix(1.0, 0.12, u_relight);
    float envLevel = (u_diffuseIntensity * mix(0.85, 1.0, up) + u_ambientIntensity) * envFade;
    vec3 ambient = diffuseColor * envLevel;
    // Environment specular reflection: metals reflect their tint, dielectrics a
    // faint 0.04; stronger on smooth surfaces, brightened at grazing angles.
    vec3 envFres = fresnelSchlick(f0, NdotV);
    vec3 ambientSpec = envFres * (1.0 - roughness) * u_fresnelIntensity * mix(0.8, 1.0, up) * envFade;

    // ── Moving key light (the interactive relight, added on top) ─────────────
    vec3 L = normalize(u_lightPos);
    vec3 Hh = normalize(L + Vv);
    float NdotL = max(dot(normal, L), 0.0);
    float NdotH = max(dot(normal, Hh), 0.0);
    float VdotH = max(dot(Vv, Hh), 0.0);

    float a = roughness * roughness;
    float a2 = a * a;
    float dn = (NdotH * NdotH) * (a2 - 1.0) + 1.0;
    float D = a2 / (3.14159265 * dn * dn + 1e-5);
    float kk = (roughness + 1.0) * (roughness + 1.0) / 8.0;
    float G = (NdotV / (NdotV * (1.0 - kk) + kk)) * (NdotL / (NdotL * (1.0 - kk) + kk));
    vec3 F = fresnelSchlick(f0, VdotH);
    vec3 keySpec = (D * G * F) / (4.0 * NdotV * NdotL + 1e-4) * NdotL * u_specularMultiplier;
    // Modest diffuse from the key light so moving it shapes the surface without
    // overpowering the neutral base; while relighting it takes over the energy
    // the environment gives up (0.45 → 1.25), so lit areas stay bright while
    // unlit areas fall off — that contrast is what sells the handover.
    vec3 keyDiffuse = diffuseColor * NdotL * u_diffuseIntensity * mix(0.45, 1.25, u_relight);

    vec3 color = ambient + ambientSpec + keyDiffuse + keySpec;
    // Subtle Phototron-cyan sheen on smooth spots (brand signature; tiny).
    color += u_accentColor * pow(NdotH, 48.0) * u_accentIntensity * (1.0 - roughness);

    // Match the 3D: PBR-Neutral tone map in linear, then encode to sRGB.
    color = pbrNeutralToneMapping(color);
    color = linearToSrgb(color);

    gl_FragColor = vec4(color, alpha);
  }
`;

export const FRAGMENT_SHADER_SIMPLE = `
  precision mediump float;
  
  varying vec2 v_texCoord;
  
  uniform sampler2D u_albedo;
  uniform sampler2D u_normal;
  uniform sampler2D u_specular;
  uniform vec3 u_lightPos;
  uniform vec2 u_textureSize;
  uniform vec2 u_canvasSize;
  
  uniform float u_ambientIntensity;
  uniform float u_diffuseIntensity;
  uniform float u_specularMultiplier;
  
  void main() {
    // Aspect ratio correction
    float textureAspect = u_textureSize.x / u_textureSize.y;
    float canvasAspect = u_canvasSize.x / u_canvasSize.y;
    vec2 uv = v_texCoord;
    
    if (canvasAspect > textureAspect) {
      float scale = textureAspect / canvasAspect;
      uv.x = (uv.x - 0.5) / scale + 0.5;
    } else {
      float scale = canvasAspect / textureAspect;
      uv.y = (uv.y - 0.5) / scale + 0.5;
    }
    
    if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
      discard;
    }
    
    vec4 albedoSample = texture2D(u_albedo, uv);
    vec3 albedo = albedoSample.rgb;
    float alpha = albedoSample.a;
    if (alpha < 0.01) discard;
    
    vec3 normal = normalize(texture2D(u_normal, uv).rgb * 2.0 - 1.0);

    // Spec → roughness (inverted gloss), dielectric.
    float gloss = dot(texture2D(u_specular, uv).rgb, vec3(0.299, 0.587, 0.114));
    float roughness = clamp(1.0 - gloss, 0.045, 1.0);

    vec3 L = normalize(u_lightPos);
    vec3 Vv = vec3(0.0, 0.0, 1.0);
    vec3 Hh = normalize(L + Vv);
    float NdotL = max(dot(normal, L), 0.0);
    float NdotH = max(dot(normal, Hh), 0.0);

    float shininess = mix(8.0, 256.0, 1.0 - roughness);
    vec3 specular = vec3(pow(NdotH, shininess) * (1.0 - roughness)) * u_specularMultiplier;

    float hemi = 0.5 + 0.5 * normal.y;
    vec3 ambient = albedo * mix(u_ambientIntensity * 0.6, u_ambientIntensity * 1.5, hemi);
    vec3 diffuse = albedo * NdotL * u_diffuseIntensity;

    vec3 color = clamp(ambient + diffuse + specular, 0.0, 1.0);
    gl_FragColor = vec4(color, alpha);
  }
`;
